interface RiskJobsModalProps {
  atRiskJobs: number;
  totalJobs: number;
  onClose: () => void;
}

// Generate anonymized risk job data
const generateRiskJobs = (count: number) => {
  const regions = ['China Mainland', 'Singapore', 'Europe', 'United States', 'Hong Kong'];
  const jobTypes = ['Incremental Backup', 'Full Backup', 'Restore', 'Replication', 'Archive'];
  const workloadTypes = ['Database', 'File Server', 'Virtual Machine', 'Application', 'Object Storage'];
  const riskReasons = [
    'Network congestion detected',
    'Large dataset size (>500GB)',
    'Resource contention',
    'Approaching backup window limit',
    'Previous job retry',
    'Storage performance degradation'
  ];

  return Array.from({ length: count }, (_, i) => ({
    id: `JOB-${String(i + 1).padStart(4, '0')}`,
    region: regions[Math.floor(Math.random() * regions.length)],
    jobType: jobTypes[Math.floor(Math.random() * jobTypes.length)],
    workloadType: workloadTypes[Math.floor(Math.random() * workloadTypes.length)],
    riskReason: riskReasons[Math.floor(Math.random() * riskReasons.length)],
    dataSize: Math.floor(Math.random() * 800 + 200), // 200-1000 GB
    timeToSLA: Math.floor(Math.random() * 180 + 60), // 60-240 minutes
  }));
};

export default function RiskJobsModal({ atRiskJobs, totalJobs, onClose }: RiskJobsModalProps) {
  const riskJobs = generateRiskJobs(atRiskJobs);
  const riskPercentage = ((atRiskJobs / totalJobs) * 100).toFixed(2);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
      <div className="bg-slate-900 border border-slate-700 rounded-lg shadow-2xl w-[900px] max-h-[85vh] overflow-hidden">
        {/* Header */}
        <div className="px-6 py-4 border-b border-slate-700 flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-white">At-Risk Jobs Overview</h3>
            <p className="text-xs text-gray-400 mt-1">
              {atRiskJobs} jobs ({riskPercentage}% of {totalJobs.toLocaleString()} active operations)
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white transition-colors cursor-pointer"
          >
            <i className="ri-close-line text-xl"></i>
          </button>
        </div>

        {/* Content */}
        <div className="px-6 py-5 overflow-y-auto max-h-[calc(85vh-140px)]">
          {/* Info Banner */}
          <div className="mb-5 p-4 bg-amber-500/10 border border-amber-500/30 rounded-lg flex items-start gap-3">
            <i className="ri-information-line text-amber-400 text-xl mt-0.5"></i>
            <div className="flex-1">
              <div className="text-sm font-medium text-amber-400 mb-1">
                Early Risk Awareness
              </div>
              <div className="text-xs text-gray-300 leading-relaxed">
                These jobs are approaching their SLA thresholds but have not breached yet. This is proactive monitoring, not an incident alert.
              </div>
            </div>
          </div>

          {/* Jobs Table */}
          <div className="space-y-2">
            <div className="grid grid-cols-12 gap-3 px-3 py-2 text-xs font-medium text-gray-400 uppercase tracking-wider border-b border-slate-700/50">
              <div className="col-span-2">Job ID</div>
              <div className="col-span-2">Region</div>
              <div className="col-span-2">Job Type</div>
              <div className="col-span-2">Workload</div>
              <div className="col-span-3">Risk Reason</div>
              <div className="col-span-1 text-right">Size</div>
            </div>

            {riskJobs.map((job) => (
              <div
                key={job.id}
                className="grid grid-cols-12 gap-3 px-3 py-3 text-xs bg-slate-800/30 hover:bg-slate-800/50 rounded border border-slate-700/30 transition-colors"
              >
                <div className="col-span-2 font-mono text-cyan-400">{job.id}</div>
                <div className="col-span-2 text-gray-300">{job.region}</div>
                <div className="col-span-2 text-gray-300">{job.jobType}</div>
                <div className="col-span-2 text-gray-300">{job.workloadType}</div>
                <div className="col-span-3 text-amber-400 text-[11px]">{job.riskReason}</div>
                <div className="col-span-1 text-right text-gray-400 font-mono">
                  {job.dataSize} GB
                </div>
              </div>
            ))}
          </div>

          {/* Summary Stats */}
          <div className="mt-6 grid grid-cols-3 gap-4">
            <div className="p-4 bg-slate-800/50 rounded border border-slate-700/50">
              <div className="text-xs text-gray-400 mb-1">Average Data Size</div>
              <div className="text-2xl font-bold text-white font-mono">
                {Math.floor(riskJobs.reduce((sum, job) => sum + job.dataSize, 0) / riskJobs.length)} GB
              </div>
            </div>
            <div className="p-4 bg-slate-800/50 rounded border border-slate-700/50">
              <div className="text-xs text-gray-400 mb-1">Avg. Time to SLA</div>
              <div className="text-2xl font-bold text-amber-400 font-mono">
                {Math.floor(riskJobs.reduce((sum, job) => sum + job.timeToSLA, 0) / riskJobs.length)} min
              </div>
            </div>
            <div className="p-4 bg-slate-800/50 rounded border border-slate-700/50">
              <div className="text-xs text-gray-400 mb-1">Most Affected Region</div>
              <div className="text-lg font-semibold text-white mt-1">
                {riskJobs.reduce((acc, job) => {
                  acc[job.region] = (acc[job.region] || 0) + 1;
                  return acc;
                }, {} as Record<string, number>)[
                  Object.entries(
                    riskJobs.reduce((acc, job) => {
                      acc[job.region] = (acc[job.region] || 0) + 1;
                      return acc;
                    }, {} as Record<string, number>)
                  ).sort((a, b) => b[1] - a[1])[0][0]
                ]}
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-slate-700 flex justify-between items-center">
          <div className="text-xs text-gray-400">
            Data anonymized for security · No customer-identifying information
          </div>
          <button
            onClick={onClose}
            className="px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white text-sm rounded transition-colors cursor-pointer whitespace-nowrap"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
